import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Award, Calendar, Clock, Phone, MapPin, Mail, 
  ChevronRight, Star, Activity, Heart, Users, 
  Globe, GraduationCap, Stethoscope, Plus, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

// Hero Section
const HeroSection = () => (
  <section className="bg-gradient-to-br from-[#0f1829] via-[#1E2A4A] to-[#2D3A5C] min-h-[90vh] relative overflow-hidden" data-testid="hero-section">
    {/* Background effects */}
    <div className="absolute inset-0">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_30%,rgba(74,111,165,0.2)_0%,transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_80%,rgba(30,42,74,0.3)_0%,transparent_50%)]" />
    </div>

    <div className="container-custom relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[90vh] py-20 lg:py-0">
        {/* Left Content */}
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={stagger}
          className="text-center lg:text-left"
        >
          <motion.div 
            variants={fadeInUp}
            className="flex items-center gap-3 justify-center lg:justify-start mb-6"
          >
            <span className="text-[10px] tracking-[0.32em] uppercase text-white/55">
              Internationally Trained Orthopaedic Specialist
            </span>
          </motion.div>

          <motion.h1 
            variants={fadeInUp}
            className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-medium text-white leading-tight mb-6"
          >
            Restoring Motion,<br />
            <em className="text-[#4A6FA5] italic">Rebuilding Lives.</em>
          </motion.h1>

          <motion.div variants={fadeInUp} className="mb-6">
            <h2 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-semibold text-white">
              Dr. Drumil D Majmundar
            </h2>
            <p className="text-sm text-white/60 mt-1 tracking-wide">MS Orthopaedics</p>
            <p className="text-sm text-[#4A6FA5] italic mt-1">
              Fellow in Hip and Knee Arthroplasty — Germany
            </p>
          </motion.div>

          <motion.div 
            variants={fadeInUp}
            className="flex items-center gap-3 justify-center lg:justify-start flex-wrap mb-8"
          >
            <span className="text-[11px] tracking-[0.18em] uppercase text-white/40">Joint Replacement</span>
            <span className="w-1 h-1 rounded-full bg-[#4A6FA5]" />
            <span className="text-[11px] tracking-[0.18em] uppercase text-white/40">Arthroscopy</span>
            <span className="w-1 h-1 rounded-full bg-[#4A6FA5]" />
            <span className="text-[11px] tracking-[0.18em] uppercase text-white/40">Sports Injury</span>
          </motion.div>

          <motion.div variants={fadeInUp} className="flex gap-4 justify-center lg:justify-start flex-wrap">
            <Link to="/book-appointment">
              <Button 
                className="bg-[#4A6FA5] hover:bg-[#3d5d8a] text-white px-8 py-6 rounded text-[11px] tracking-[0.2em] uppercase font-medium transition-all hover:-translate-y-1"
                data-testid="hero-book-btn"
              >
                Book Consultation
              </Button>
            </Link>
            <a href="#about">
              <Button 
                variant="outline" 
                className="border-white/20 text-white/65 hover:border-white hover:text-white px-8 py-6 rounded text-[11px] tracking-[0.2em] uppercase font-medium"
              >
                About Dr. Majmundar
              </Button>
            </a>
          </motion.div>
        </motion.div>

        {/* Right Content - Doctor Photo */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex justify-center"
        >
          <div className="relative">
            <div className="w-72 h-80 md:w-80 md:h-96 rounded-[180px_180px_160px_160px] overflow-hidden border-2 border-[#4A6FA5]/30 shadow-[0_32px_80px_rgba(0,0,0,0.35),0_0_0_12px_rgba(255,255,255,0.04)]">
              <img
                src="https://customer-assets.emergentagent.com/job_precision-joints-1/artifacts/kc2ozkrj_IMG_20230707_113443%20%281%29.jpg"
                alt="Dr. Drumil D Majmundar"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Stats */}
            <div className="flex justify-center gap-8 mt-8">
              {[
                { value: '10,000+', label: 'Surgeries' },
                { value: '9+', label: 'Years' },
                { value: '2', label: 'Fellowships' }
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-medium text-white">
                    {stat.value}
                  </div>
                  <div className="text-[9px] tracking-[0.18em] uppercase text-white/30 mt-1">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

// Trust Band Section
const TrustBand = () => {
  const trustItems = [
    { icon: GraduationCap, title: 'MS Orthopaedics', subtitle: 'B.J. Medical College, Ahmedabad', color: '#4A6FA5' },
    { icon: Activity, title: '10,000+ Surgeries', subtitle: '9+ years dedicated practice', color: '#2a6b68' },
    { icon: Globe, title: 'International Fellowship', subtitle: 'OCM Munich, Germany · 2023', color: '#4A6FA5' },
    { icon: Award, title: 'Asst. Professor', subtitle: 'SAL Institute of Medical Sciences', color: '#3a6a8a' },
    { icon: MapPin, title: '317 Maple Trade Centre', subtitle: 'Memnagar, Ahmedabad', color: '#4a5568' }
  ];

  return (
    <section className="bg-[#1E2A4A] border-b border-[#4A6FA5]/12" data-testid="trust-band">
      <div className="container-custom">
        <div className="flex flex-wrap items-stretch justify-center">
          {trustItems.map((item, index) => (
            <div 
              key={item.title}
              className="flex items-center gap-3 px-6 py-5 border-r border-white/[0.06] last:border-r-0 flex-1 min-w-[200px] justify-center"
            >
              <div 
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: item.color }}
              >
                <item.icon className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-xs font-medium text-white">{item.title}</div>
                <div className="text-[10px] text-white/35">{item.subtitle}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => (
  <section id="about" className="section-padding bg-[#FDFBF8]" data-testid="about-section">
    <div className="container-custom">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
        {/* Left Content */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
        >
          <motion.div variants={fadeInUp} className="section-tag mb-4">
            About the Doctor
          </motion.div>
          
          <motion.h2 
            variants={fadeInUp}
            className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-medium text-[#1E2A4A] leading-tight mb-6"
          >
            Precision care,<br /><em className="text-[#4A6FA5] italic">human touch.</em>
          </motion.h2>

          <motion.p variants={fadeInUp} className="text-sm text-[#5e7080] leading-relaxed mb-4">
            Dr. Drumil D Majmundar is an MS Orthopaedics specialist with over 9 years of dedicated practice 
            in joint replacement, arthroscopy, and sports injury treatment. He has performed more than 
            10,000 successful surgeries — combining world-class technical training with genuine compassion 
            for every patient.
          </motion.p>

          <motion.p variants={fadeInUp} className="text-sm text-[#5e7080] leading-relaxed mb-6">
            Currently serving as Assistant Professor at SAL Institute of Medical Sciences, Ahmedabad, 
            Dr. Majmundar brings both clinical excellence and academic rigour to his practice. His 
            international training at OCM Munich, Germany — one of Europe's leading orthopaedic centres 
            — ensures his patients receive globally benchmarked care.
          </motion.p>

          {/* Training Badges */}
          <motion.div variants={fadeInUp} className="space-y-3">
            <div className="flex items-center gap-4 p-4 rounded-xl border border-[#1E2A4A]/12 bg-[#1E2A4A]/[0.04]">
              <span className="text-2xl">🇩🇪</span>
              <div>
                <div className="text-sm font-medium text-[#1E2A4A]">
                  International Fellowship · Hip & Knee Arthroplasty
                </div>
                <div className="text-xs text-[#7A8FA0]">
                  OCM Orthopädische Chirurgie München, Germany · 2023
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 rounded-xl border border-[#4A6FA5]/20 bg-[#4A6FA5]/[0.06]">
              <span className="text-2xl">🇮🇳</span>
              <div>
                <div className="text-sm font-medium text-[#1E2A4A]">
                  MS Orthopaedics · Indian Fellowship — Knee Arthroscopy & Arthroplasty
                </div>
                <div className="text-xs text-[#7A8FA0]">
                  B.J. Medical College & Civil Hospital, Ahmedabad
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Right Content - Quote Card */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="bg-[#1E2A4A] rounded-2xl p-8 md:p-10 relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#4A6FA5]/12" />
            
            <svg width="38" height="38" viewBox="0 0 100 100" fill="none" className="relative z-10">
              <path d="M50 8 L78 20 L78 48 Q78 72 50 84 Q22 72 22 48 L22 20 Z" fill="rgba(232,213,176,.07)"/>
              <path d="M50 8 L78 20 L78 48 Q78 72 50 84 Q22 72 22 48 L22 20 Z" stroke="#E8EEF5" strokeWidth="2.5" fill="none" opacity=".5"/>
              <line x1="50" y1="28" x2="50" y2="64" stroke="#4A6FA5" strokeWidth="3.5" strokeLinecap="round"/>
              <line x1="33" y1="46" x2="67" y2="46" stroke="#4A6FA5" strokeWidth="3.5" strokeLinecap="round"/>
              <circle cx="50" cy="46" r="6" fill="#4A6FA5"/>
            </svg>

            <p className="font-['Cormorant_Garamond'] text-xl md:text-2xl font-normal text-[#E8EEF5] leading-relaxed italic mt-4 mb-4 relative z-10">
              "Every patient deserves the best possible outcome — achieved with the least possible intervention."
            </p>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#E8EEF5]/30 relative z-10">
              — Dr. Drumil D Majmundar
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-[#E8EEF5]/10">
              {[
                { value: '10,000+', label: 'Successful Surgeries' },
                { value: '9+', label: 'Years Experience' },
                { value: '🥇', label: 'Gold Medal — Gujarat University' },
                { value: '4', label: 'Research Publications' }
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="font-['Cormorant_Garamond'] text-3xl font-medium text-[#4A6FA5]">
                    {stat.value}
                  </div>
                  <div className="text-[10px] text-[#E8EEF5]/30 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Memberships & Awards */}
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="rounded-xl p-5 bg-[#F5F7FA]">
              <div className="text-[11px] tracking-[0.2em] uppercase text-[#4A6FA5] mb-3">
                Memberships
              </div>
              <ul className="space-y-2">
                {['Indian Orthopaedic Association', 'Gujarat Orthopaedic Association', 'Ahmedabad Orthopaedic Society'].map((item) => (
                  <li key={item} className="text-xs text-[#1E2A4A] flex items-start gap-2">
                    <span className="text-[#4A6FA5]">·</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl p-5 bg-[#F5F7FA]">
              <div className="text-[11px] tracking-[0.2em] uppercase text-[#4A6FA5] mb-3">
                Awards
              </div>
              <ul className="space-y-2">
                {[
                  '1st Prize — GOA Quiz 2018',
                  'Represented Gujarat at IOA Quiz',
                  'Gold Medal — Pathology 2013'
                ].map((item) => (
                  <li key={item} className="text-xs text-[#1E2A4A] flex items-start gap-2">
                    <span className="text-[#4A6FA5]">·</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

// Specialties Section
const SpecialtiesSection = () => {
  const specialties = [
    {
      icon: Plus,
      title: 'Total & Partial Joint Replacement',
      description: 'Knee and hip joint replacement — including Robotic TKR and UKR — using the latest minimally invasive techniques for precise implant positioning and faster recovery.',
      tag: 'Robotic TKR · UKR',
      color: '#4A6FA5'
    },
    {
      icon: Activity,
      title: 'Arthroscopic Surgery',
      description: 'Keyhole surgery to diagnose and treat knee and shoulder conditions — including meniscus tears, ligament injuries, and cartilage damage — with minimal scarring.',
      tag: 'Minimally Invasive',
      color: '#4A6FA5'
    },
    {
      icon: Heart,
      title: 'Sports Injury Treatment',
      description: 'ACL & PCL reconstruction, meniscus repair, shoulder instability, and tendon injuries — getting athletes and active patients back to their sport safely.',
      tag: 'Sports Orthopaedics',
      color: '#1E2A4A'
    },
    {
      icon: Stethoscope,
      title: 'Revision Joint Replacement',
      description: 'Complex revision surgeries for failed or worn implants — including international-fellowship-trained expertise in primary and revision arthroplasty.',
      tag: 'Complex Revision',
      color: '#4A6FA5'
    },
    {
      icon: Users,
      title: 'Trauma & Fracture Care',
      description: 'Comprehensive management of orthopaedic trauma including fracture fixation, complex fractures, and post-trauma rehabilitation with evidence-based protocols.',
      tag: 'Trauma Surgery',
      color: '#4A6FA5'
    },
    {
      icon: Award,
      title: 'Geriatric Joint Care',
      description: 'Tailored surgical and non-surgical plans for older patients with joint degeneration — considering individual health, activity levels, and recovery goals.',
      tag: 'Elder Orthopaedics',
      color: '#1E2A4A'
    }
  ];

  return (
    <section id="specialties" className="section-padding bg-[#F5F7FA]" data-testid="specialties-section">
      <div className="container-custom">
        <div className="text-center max-w-xl mx-auto mb-12">
          <div className="section-tag justify-center mb-4">
            <span className="w-5 h-px bg-[#4A6FA5]" />
            Areas of Expertise
          </div>
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-medium text-[#1E2A4A]">
            What we treat
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {specialties.map((specialty, index) => (
            <motion.div
              key={specialty.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="specialty-card bg-white rounded-2xl p-8"
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                style={{ backgroundColor: `${specialty.color}15` }}
              >
                <specialty.icon className="w-6 h-6" style={{ color: specialty.color }} />
              </div>
              <h3 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#1E2A4A] mb-3">
                {specialty.title}
              </h3>
              <p className="text-sm text-[#7A8FA0] leading-relaxed mb-4">
                {specialty.description}
              </p>
              <span 
                className="inline-block text-[9px] tracking-[0.18em] uppercase px-3 py-1 rounded-full border"
                style={{ color: specialty.color, borderColor: `${specialty.color}40` }}
              >
                {specialty.tag}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Choose Section
const WhyChooseSection = () => {
  const reasons = [
    { icon: Globe, title: 'International Fellowship · Germany', desc: 'Primary & Revision Hip/Knee Arthroplasty — OCM Munich, 2023' },
    { icon: Activity, title: '10,000+ Surgeries', desc: 'Extensive experience across joint replacement, arthroscopy & trauma' },
    { icon: GraduationCap, title: 'Academic Excellence', desc: 'Assistant Professor · Gold Medallist · Published Researcher' },
    { icon: Heart, title: 'Minimally Invasive First', desc: 'Every non-surgical option explored before recommending a procedure' },
    { icon: Clock, title: 'Faster Recovery Protocols', desc: 'Most patients mobile within 24 hours of surgery' },
    { icon: Users, title: 'Direct Doctor Access', desc: 'Dr. Majmundar personally reachable during recovery' }
  ];

  return (
    <section className="section-padding bg-[#1E2A4A] relative overflow-hidden" data-testid="why-choose-section">
      <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#4A6FA5]/10" />
      
      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#4A6FA5] mb-4">
              Why Choose Dr. Majmundar
            </div>
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-medium text-[#E8EEF5] leading-tight mb-4">
              World-class training.<br />Local, personal care.
            </h2>
            <p className="text-sm text-[#E8EEF5]/40 leading-relaxed">
              Dr. Majmundar brings together international surgical expertise, academic excellence, 
              and a genuine commitment to patient wellbeing — offering Ahmedabad patients globally 
              benchmarked orthopaedic care, close to home.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {reasons.map((reason, index) => (
              <motion.div
                key={reason.title}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-4 items-start p-5 bg-white/[0.04] rounded-xl border border-[#E8EEF5]/[0.06] hover:bg-white/[0.07] transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-[#4A6FA5]/20 flex items-center justify-center flex-shrink-0">
                  <reason.icon className="w-4 h-4 text-[#4A6FA5]" />
                </div>
                <div>
                  <div className="text-xs font-medium text-[#E8EEF5] mb-1">{reason.title}</div>
                  <div className="text-[11px] text-[#E8EEF5]/35 leading-relaxed">{reason.desc}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Experience Section
const ExperienceSection = () => {
  const positions = [
    { id: 'sal-2025', role: 'Assistant Professor', place: 'SAL Institute of Medical Sciences', loc: 'Ahmedabad', date: 'Feb 2025 – Present', active: true },
    { id: 'bj-2021', role: 'Assistant Professor', place: 'BJ Medical College & Civil Hospital', loc: 'Ahmedabad', date: 'Jul 2021 – Jun 2022' },
    { id: 'mp-2021', role: 'Assistant Professor', place: 'MP Shah Medical College', loc: 'Jamnagar', date: 'Feb 2021 – Jun 2021' },
    { id: 'bj-senior-2019', role: 'Senior Resident Doctor', place: 'BJ Medical College & Civil Hospital', loc: 'Ahmedabad', date: 'May 2019 – Jul 2020' },
    { id: 'bj-junior-2016', role: 'Junior Resident Doctor', place: 'BJ Medical College & Civil Hospital', loc: 'Ahmedabad', date: 'Apr 2016 – Apr 2019' }
  ];

  return (
    <section id="experience" className="section-padding bg-[#FDFBF8]" data-testid="experience-section">
      <div className="container-custom">
        <div className="section-tag mb-4">Career & Training</div>
        <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-medium text-[#1E2A4A] mb-12">
          Professional <em className="text-[#4A6FA5] italic">experience</em>
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Timeline */}
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#4A6FA5] mb-5 flex items-center gap-3">
              Professional Positions
              <span className="flex-1 h-px bg-[#4A6FA5]/25" />
            </div>
            <div className="space-y-0">
              {positions.map((pos) => (
                <div key={pos.id} className="timeline-item flex gap-4 pb-6">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1 ${
                    pos.active 
                      ? 'bg-[#1E2A4A] border-2 border-[#4A6FA5]' 
                      : 'bg-[#F5F7FA] border-2 border-[#4A6FA5]/30'
                  }`}>
                    {pos.active ? (
                      <ChevronRight className="w-3 h-3 text-[#4A6FA5]" />
                    ) : (
                      <div className="w-1.5 h-1.5 rounded-full bg-[#7A8FA0]" />
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-[#1E2A4A]">{pos.role}</div>
                    <div className="text-xs text-[#4A6FA5]">{pos.place}</div>
                    <div className="text-[11px] text-[#7A8FA0] mt-0.5">{pos.loc}</div>
                    <div className="text-[10px] tracking-wider text-[#4A6FA5] mt-1">{pos.date}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Fellowships */}
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#4A6FA5] mb-5">
              Fellowships
            </div>
            <div className="space-y-4">
              <div className="bg-[#1E2A4A] rounded-xl p-5 flex gap-4">
                <span className="text-2xl">🇩🇪</span>
                <div>
                  <div className="text-sm font-medium text-[#E8EEF5]">
                    International Fellowship — Primary & Revision Hip & Knee Arthroplasty
                  </div>
                  <div className="text-xs text-[#E8EEF5]/50 mt-1">
                    OCM Orthopädische Chirurgie München, Germany
                  </div>
                  <span className="inline-block mt-2 text-[10px] tracking-[0.2em] uppercase bg-[#E8EEF5]/10 text-[#4A6FA5] px-3 py-1 rounded-full">
                    2023
                  </span>
                </div>
              </div>
              <div className="bg-[#F5F7FA] rounded-xl p-5 flex gap-4">
                <span className="text-2xl">🇮🇳</span>
                <div>
                  <div className="text-sm font-medium text-[#1E2A4A]">
                    Indian Fellowship — Knee Arthroscopy & Arthroplasty
                  </div>
                  <div className="text-xs text-[#7A8FA0] mt-1">
                    Advanced training within India's premier orthopaedic institutions
                  </div>
                  <span className="inline-block mt-2 text-[10px] tracking-[0.2em] uppercase bg-[#4A6FA5]/12 text-[#4A6FA5] px-3 py-1 rounded-full">
                    India
                  </span>
                </div>
              </div>
            </div>

            <div className="text-[10px] tracking-[0.28em] uppercase text-[#4A6FA5] mt-8 mb-5">
              Awards & Recognition
            </div>
            <div className="bg-[#F5F7FA] rounded-xl p-5">
              <ul className="space-y-2">
                {[
                  '🥇 Gold Medal in Pathology — Gujarat University (2013)',
                  '🏆 1st Prize — Gujarat Orthopaedic Association Quiz (2018)',
                  '🌟 Represented Gujarat at IOA Quiz, Coimbatore (2018)'
                ].map((award) => (
                  <li key={award} className="text-xs text-[#1E2A4A] flex items-start gap-2">
                    {award}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section - Links to Google My Business Reviews
const TestimonialsSection = () => {
  return (
    <section className="section-padding bg-[#FDFBF8]" data-testid="testimonials-section">
      <div className="container-custom">
        <div className="text-center max-w-xl mx-auto mb-12">
          <div className="section-tag justify-center mb-4">
            <span className="w-5 h-px bg-[#4A6FA5]" />
            Patient Stories
          </div>
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-medium text-[#1E2A4A]">
            What our patients <em className="text-[#4A6FA5] italic">say</em>
          </h2>
        </div>

        {/* Google Reviews Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto"
        >
          <a 
            href="https://share.google/F5A4hY3YAeRmKazaF"
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border border-[#E8EEF5]/20 group"
          >
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Doctor Photo & Info */}
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-[#4A6FA5]/30 shadow-lg">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_precision-joints-1/artifacts/kc2ozkrj_IMG_20230707_113443%20%281%29.jpg"
                    alt="Dr. Drumil Majmundar"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#1E2A4A]">
                    Dr. Drumil Majmundar
                  </h3>
                  <p className="text-sm text-[#7A8FA0]">Avyan Orthopaedics</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex text-[#4A6FA5] text-lg">
                      {'★★★★★'}
                    </div>
                    <span className="text-sm text-[#4A6FA5] font-medium">5.0</span>
                  </div>
                </div>
              </div>

              {/* Google Logo & CTA */}
              <div className="flex-1 text-center md:text-right">
                <img 
                  src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" 
                  alt="Google" 
                  className="h-8 object-contain mx-auto md:ml-auto md:mr-0 mb-3"
                />
                <p className="text-sm text-[#5e7080] mb-3">
                  Read verified patient reviews on Google
                </p>
                <span className="inline-flex items-center gap-2 text-[#4A6FA5] font-medium group-hover:text-[#1E2A4A] transition-colors">
                  View All Reviews
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </div>

            {/* Sample Review Preview */}
            <div className="mt-6 pt-6 border-t border-[#E8EEF5]/30">
              <p className="text-sm text-[#5e7080] italic leading-relaxed">
                "Dr. Majmundar is an exceptional orthopaedic surgeon. His expertise in joint replacement and caring approach made my surgery and recovery experience smooth. Highly recommend to anyone seeking quality orthopaedic care in Ahmedabad."
              </p>
              <p className="text-xs text-[#7A8FA0] mt-3">
                — Verified Google Review
              </p>
            </div>
          </a>
        </motion.div>

        {/* Trust Indicators */}
        <div className="flex flex-wrap items-center justify-center gap-8 mt-10">
          <div className="flex items-center gap-2 text-sm text-[#7A8FA0]">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4A6FA5" strokeWidth="2" strokeLinecap="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            Verified Reviews
          </div>
          <div className="flex items-center gap-2 text-sm text-[#7A8FA0]">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4A6FA5" strokeWidth="2" strokeLinecap="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            10,000+ Surgeries
          </div>
          <div className="flex items-center gap-2 text-sm text-[#7A8FA0]">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4A6FA5" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M2 12h20" />
              <path d="M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20" />
            </svg>
            International Fellowship
          </div>
        </div>
      </div>
    </section>
  );
};

// Google Reviews Section
const GoogleReviewsSection = () => (
  <section className="section-padding bg-[#1E2A4A]" data-testid="google-reviews-section">
    <div className="container-custom">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#4A6FA5] mb-4">
            Patient Ratings
          </div>
          <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-medium text-[#E8EEF5] leading-tight mb-4">
            Trusted by patients<br />across <em className="text-[#4A6FA5] italic">Ahmedabad</em>
          </h2>
          <p className="text-sm text-[#E8EEF5]/40 leading-relaxed mb-6">
            Real ratings from verified patients on Google — India's most trusted platform for finding specialist doctors.
          </p>
          <div className="flex items-center gap-4">
            <a 
              href="tel:+917043543985"
              className="inline-flex items-center gap-2 bg-[#4A6FA5] hover:bg-[#3d5d8a] text-white px-6 py-3 rounded-lg text-sm font-medium transition-colors"
            >
              <Phone className="w-4 h-4" />
              Call Now
            </a>
            <a 
              href="https://wa.me/917043543985?text=Hello%20Dr.%20Majmundar%2C%20I%20would%20like%20to%20book%20a%20consultation."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-[#E8EEF5]/30 text-[#E8EEF5] hover:bg-white/10 px-6 py-3 rounded-lg text-sm font-medium transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor">
                <path d="M16 0C7.164 0 0 7.163 0 16c0 2.833.737 5.494 2.027 7.807L.069 31.07a1 1 0 001.212 1.212l7.264-1.957A15.936 15.936 0 0016 32c8.836 0 16-7.164 16-16S24.836 0 16 0zm8.322 22.293c-.346.974-1.719 1.782-2.813 2.017-.748.161-1.724.289-5.009-1.077-4.199-1.726-6.907-5.986-7.115-6.263-.201-.277-1.683-2.239-1.683-4.269 0-2.031 1.065-3.026 1.443-3.442.378-.416.824-.52 1.099-.52.274 0 .549.003.789.014.253.012.592-.096.927.707.346.826 1.173 2.856 1.275 3.064.103.207.172.449.034.725-.138.277-.207.449-.414.691-.207.243-.435.542-.621.728-.207.207-.422.431-.181.845.241.414 1.071 1.767 2.3 2.862 1.582 1.409 2.916 1.845 3.33 2.052.414.207.656.172.897-.104.241-.276 1.032-1.204 1.308-1.618.276-.414.552-.345.931-.207.379.138 2.403 1.134 2.817 1.341.414.207.69.311.793.483.103.173.103.999-.243 1.97z"/>
              </svg>
              WhatsApp
            </a>
          </div>
        </div>
        
        {/* Google Reviews Embed */}
        <div className="bg-white/5 border border-[#E8EEF5]/20 rounded-2xl p-6 overflow-hidden">
          <div className="flex items-center gap-3 mb-6">
            <img 
              src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" 
              alt="Google" 
              className="h-6 object-contain"
            />
            <span className="text-[#E8EEF5]/60 text-sm">Reviews</span>
          </div>
          
          {/* Google My Business Profile Link */}
          <a 
            href="https://share.google/F5A4hY3YAeRmKazaF"
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-[#1E2A4A]/50 rounded-xl p-6 border border-[#E8EEF5]/10 hover:border-[#4A6FA5]/30 transition-colors"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#4A6FA5]/30">
                <img 
                  src="https://customer-assets.emergentagent.com/job_precision-joints-1/artifacts/kc2ozkrj_IMG_20230707_113443%20%281%29.jpg"
                  alt="Dr. Drumil Majmundar"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="text-[#E8EEF5] font-semibold">Dr. Drumil Majmundar</h3>
                <p className="text-[#E8EEF5]/50 text-sm">Avyan Orthopaedics</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 mb-3">
              <div className="flex text-[#4A6FA5]">
                {'★★★★★'}
              </div>
              <span className="text-[#E8EEF5]/60 text-sm">5.0 rating</span>
            </div>
            
            <p className="text-[#E8EEF5]/40 text-sm mb-4">
              Click to view all Google reviews and leave your feedback
            </p>
            
            <div className="flex items-center gap-2 text-[#4A6FA5] text-sm font-medium">
              View on Google
              <ArrowRight className="w-4 h-4" />
            </div>
          </a>
          
          <p className="text-[#E8EEF5]/30 text-xs mt-4 text-center">
            317, Maple Trade Centre, Memnagar, Ahmedabad
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Process Section
const ProcessSection = () => {
  const steps = [
    { num: '1', title: 'Consultation', desc: 'Thorough assessment of your symptoms, history, imaging — with time to ask every question.' },
    { num: '2', title: 'Diagnosis & Plan', desc: 'Honest diagnosis with a personalised treatment plan — always minimal intervention first.' },
    { num: '3', title: 'Procedure', desc: 'Advanced minimally invasive surgery at a leading Ahmedabad facility, highest safety standards.' },
    { num: '4', title: 'Recovery & Beyond', desc: 'Structured physio support and follow-up until you are fully back on your feet.' }
  ];

  return (
    <section className="section-padding bg-[#FDFBF8]" data-testid="process-section">
      <div className="container-custom">
        <div className="section-tag mb-4">Patient Journey</div>
        <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-medium text-[#1E2A4A] mb-12">
          Your path to <em className="text-[#4A6FA5] italic">recovery</em>
        </h2>

        <div className="process-steps grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {steps.map((step, index) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center relative z-10"
            >
              <div className={`w-14 h-14 rounded-full mx-auto flex items-center justify-center font-['Cormorant_Garamond'] text-xl font-medium mb-4 ${
                index === 0 
                  ? 'bg-white border-2 border-[#4A6FA5] text-[#1E2A4A]'
                  : index === steps.length - 1
                  ? 'bg-[#1E2A4A] border-2 border-[#1E2A4A] text-[#E8EEF5]'
                  : 'bg-white border-2 border-[#4A6FA5] text-[#1E2A4A]'
              }`}>
                {step.num}
              </div>
              <h4 className="text-sm font-medium text-[#1E2A4A] mb-2">{step.title}</h4>
              <p className="text-xs text-[#7A8FA0] leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const faqs = [
    {
      id: 'faq-replacement',
      q: 'When do I actually need a knee or hip replacement?',
      a: 'A replacement is considered when pain significantly limits daily activities and conservative treatments — physiotherapy, medications, injections — no longer provide relief. Dr. Majmundar always exhausts non-surgical options first. Robotic TKR and UKR are available for precise, personalised outcomes.'
    },
    {
      id: 'faq-robotic-tkr',
      q: 'What is the difference between Robotic TKR and conventional knee replacement?',
      a: 'Robotic-assisted Total Knee Replacement (TKR) uses a robotic arm guided by a real-time CT-based plan to place the implant with sub-millimetre precision. This results in better alignment, less soft tissue damage, and a more natural-feeling knee compared to conventional surgery. UKR (Unicompartmental Knee Replacement) replaces only the damaged compartment of the knee.'
    },
    {
      id: 'faq-recovery',
      q: 'How long does recovery take after knee replacement?',
      a: 'Most patients walk with support within 24 hours of surgery. Light daily activities resume in 4–6 weeks and most normal activities by 3 months. Full recovery is typically achieved by 6–12 months. Minimally invasive techniques significantly reduce recovery time.'
    },
    {
      id: 'faq-arthroscopy',
      q: 'What is arthroscopy and is it suitable for me?',
      a: 'Arthroscopy is a minimally invasive keyhole procedure used to diagnose and treat conditions inside a joint — most commonly the knee and shoulder. It involves tiny incisions, a small camera, and specialised instruments. Recovery is far faster than open surgery, with most patients going home the same day or next.'
    },
    {
      id: 'faq-sports',
      q: 'Can sports injuries be fully healed? Will I return to my sport?',
      a: 'In most cases, yes — with the right surgical technique and structured rehabilitation. ACL reconstructions, meniscus repairs, and shoulder stabilisation have excellent return-to-sport rates. Dr. Majmundar has helped many athletes return to full activity following proper recovery protocols.'
    },
    {
      id: 'faq-booking',
      q: 'How do I book a consultation?',
      a: 'Fill out the appointment form below, call +91-7043543985, or message directly on WhatsApp. Clinic hours: Monday – Saturday, 10:00 AM – 7:00 PM. Address: 3rd Floor, 317 Maple Trade Centre, Memnagar, Ahmedabad.'
    }
  ];

  return (
    <section id="faq" className="section-padding bg-[#F5F7FA]" data-testid="faq-section">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="section-tag justify-center mb-4">
              <span className="w-5 h-px bg-[#4A6FA5]" />
              Common Questions
            </div>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-medium text-[#1E2A4A]">
              Frequently asked <em className="text-[#4A6FA5] italic">questions</em>
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq) => (
              <AccordionItem 
                key={faq.id} 
                value={faq.id}
                className="bg-white rounded-xl border border-[#4A6FA5]/12 overflow-hidden"
              >
                <AccordionTrigger className="px-6 py-5 text-left text-sm font-medium text-[#1E2A4A] hover:no-underline hover:bg-[#4A6FA5]/[0.04]">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-5 text-sm text-[#5e7080] leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    concern: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      setFormData({ name: '', phone: '', concern: '', message: '' });
      toast.success('Request sent! We will contact you shortly.');
    } catch (error) {
      toast.error('Failed to send request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="section-padding bg-[#FDFBF8]" data-testid="contact-section">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left - Contact Info */}
          <div>
            <div className="section-tag mb-4">Get In Touch</div>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-medium text-[#1E2A4A] mb-8">
              Book your<br /><em className="text-[#4A6FA5] italic">consultation</em> today.
            </h2>

            <div className="space-y-4 mb-8">
              {[
                { icon: MapPin, label: 'Clinic Address', value: '3rd Floor, 317 Maple Trade Centre, near Sun N Step Club, Memnagar, Ahmedabad, Gujarat 380052' },
                { icon: Phone, label: 'Phone / WhatsApp', value: '+91-7043543985 / +91-9408759177' },
                { icon: Mail, label: 'Email', value: 'drumil24@gmail.com' },
                { icon: Clock, label: 'Clinic Hours', value: 'Mon – Sat · 10:00 AM – 7:00 PM' }
              ].map((item) => (
                <div key={item.label} className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#F5F7FA] flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-4 h-4 text-[#4A6FA5]" />
                  </div>
                  <div>
                    <div className="text-[10px] tracking-wide uppercase text-[#7A8FA0] mb-1">
                      {item.label}
                    </div>
                    <div className="text-sm text-[#1E2A4A] font-medium">
                      {item.value}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Map */}
            <div className="map-wrapper rounded-2xl overflow-hidden h-56">
              <iframe
                src="https://maps.google.com/maps?q=317+Maple+Trade+Centre+Memnagar+Ahmedabad&output=embed"
                allowFullScreen
                loading="lazy"
                title="Clinic Location"
                className="w-full h-full"
              />
            </div>
          </div>

          {/* Right - Form */}
          <div>
            {success ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-[#f0f7f6] border border-[#4A6FA5]/30 rounded-2xl p-10 text-center"
              >
                <div className="text-5xl mb-4">✅</div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#1E2A4A] mb-2">
                  Request Sent!
                </h3>
                <p className="text-sm text-[#7A8FA0]">
                  Dr. Majmundar's team will call you back shortly.<br />
                  For urgent queries, WhatsApp <strong>+91-7043543985</strong> directly.
                </p>
                <Button 
                  onClick={() => setSuccess(false)}
                  className="mt-6"
                  variant="outline"
                >
                  Send Another Request
                </Button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[10px] tracking-wide uppercase text-[#7A8FA0]">
                      Full Name
                    </Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your name"
                      required
                      className="mt-1 bg-white border-[#e0d9cf] focus:border-[#4A6FA5]"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-wide uppercase text-[#7A8FA0]">
                      Phone
                    </Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+91 XXXXX XXXXX"
                      required
                      className="mt-1 bg-white border-[#e0d9cf] focus:border-[#4A6FA5]"
                      data-testid="contact-phone"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] tracking-wide uppercase text-[#7A8FA0]">
                    Concern / Condition
                  </Label>
                  <Input
                    value={formData.concern}
                    onChange={(e) => setFormData({ ...formData, concern: e.target.value })}
                    placeholder="e.g. Knee pain, ACL tear, Joint replacement…"
                    className="mt-1 bg-white border-[#e0d9cf] focus:border-[#4A6FA5]"
                    data-testid="contact-concern"
                  />
                </div>
                <div>
                  <Label className="text-[10px] tracking-wide uppercase text-[#7A8FA0]">
                    Message
                  </Label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell us about your symptoms or preferred appointment time…"
                    rows={4}
                    className="mt-1 bg-white border-[#e0d9cf] focus:border-[#4A6FA5]"
                    data-testid="contact-message"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[#1E2A4A] hover:bg-[#4A6FA5] text-[#E8EEF5] px-8 py-6 rounded text-[11px] tracking-[0.2em] uppercase font-medium"
                  data-testid="contact-submit"
                >
                  {loading ? 'Sending...' : 'Request Appointment →'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Main Home Component
const Home = () => {
  useEffect(() => {
    // Seed blog posts on first load
    const seedBlog = async () => {
      try {
        await axios.post(`${API}/seed/blog`);
      } catch (error) {
        // Ignore if already seeded
      }
    };
    seedBlog();
  }, []);

  return (
    <div data-testid="home-page">
      <HeroSection />
      <TrustBand />
      <AboutSection />
      <SpecialtiesSection />
      <WhyChooseSection />
      <ExperienceSection />
      <TestimonialsSection />
      <GoogleReviewsSection />
      <ProcessSection />
      <FAQSection />
      <ContactSection />
    </div>
  );
};

export default Home;
